import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ExternalLink,
  Film,
  Pencil,
  Plus,
  Search,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { ApiError, apiFetch, getAdminTeams } from "@/lib/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableEmptyRow,
  DataTableHead,
  DataTableHeadCell,
  DataTableRow,
  DataTableScroll,
  DataTableShell,
} from "@/components/ui/DataTable";
import { unscopedKey } from "@/lib/queryClient";
import type { Match, Season, AdminTeamRow, MeTeam } from "@/lib/types";
import { useMe } from "@/features/auth/useMe";
import { useAuthStore } from "@/stores/auth";
import { MatchEditModal } from "@/features/matches/components/MatchEditModal";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { useUploadQueueContext } from "@/features/coach/components/UploadQueueContext";
import type { UploadSlot } from "@/features/coach/components/useUploadQueue";
import {
  defaultSlotForMatch,
  SLOT_LABELS,
  slotsForMatch,
} from "@/features/matches/matchSlots";
import { useTranscodeProgress } from "../hooks/useAdminData";
import {
  AdminIconButton,
  AdminIconLink,
} from "@/components/console/AdminIconButton";
import { AdminMetricTile } from "@/components/console/AdminMetricTile";
import { AdminPageHeader } from "@/components/console/AdminPageHeader";
import { MatchSlotPanel } from "./MatchSlotPanel";
import { shootoutLabel } from "@/features/matches/matchResult";
import {
  competitionLabel,
  knownTournamentNames,
} from "@/features/matches/matchCompetition";

/**
 * M8a B1–B9 — `/admin/matches`.
 *
 * Library table (8 columns) + filters + per-row expand to a slot
 * diagnostics panel with the operator recovery action set:
 *   - Verify / Regen HLS / Re-transcode / Retry / Force Re-transcode
 *   - Logs / Regenerate Thumbnail
 *   - Delete match (B7)
 *
 * VOD viewer counts read from the shell's already-running
 * (Viewer telemetry left this page for the operator console.)
 * Live transcode progress is polled ONLY while at least one slot
 * is `transcoding` (the route gate avoids polling when idle).
 *
 * SCOPE — `/admin/matches` is visible to **global admins, team
 * admins, and uploaders**. It behaves like `/admin/people`: the
 * match row owns its team assignment, global admins can see/filter
 * all teams, and team-scoped operators only see teams they can
 * manage. Add/edit receives the same allowed-team list, so the page
 * does not depend on the admin shell's global team scope switcher.
 *
 * Calling `/api/matches` without `team_id` for a non-global user
 * would return the global recent-matches list and leak cross-tenant
 * match metadata, so non-global queries always pass scope.
 *
 * The Add/Edit modal (B6) includes a team selector for create;
 * existing matches keep their current team fixed.
 */
/** Stable empty-array identity so the `me.seasons` selector doesn't
 * return a fresh `[]` each render (which would re-fire `useMemo`). */
const EMPTY_SEASONS: Season[] = [];

type MatchTeamOption = Pick<AdminTeamRow | MeTeam, "id" | "name">;

const PAGE_SIZE_OPTIONS = [
  { value: "5", label: "5 / page" },
  { value: "10", label: "10 / page" },
  { value: "25", label: "25 / page" },
  { value: "50", label: "50 / page" },
];

function MatchFilterField({
  label,
  className = "",
  children,
}: {
  label: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <label className={`admin-match-filter-field ${className}`.trim()}>
      <span>{label}</span>
      {children}
    </label>
  );
}

function normalizeTeamOptions<T extends MatchTeamOption>(
  teams: T[],
): Array<{ id: string; name: string }> {
  const byId = new Map<string, { id: string; name: string }>();
  for (const team of teams) {
    byId.set(team.id, { id: team.id, name: team.name });
  }
  return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
}

export function AdminMatchesPage() {
  const me = useMe().data;
  const isGlobalAdmin = !!me?.user?.is_global_admin;
  // A pure club_admin has NO team_user_memberships row, so `me.teams` is empty.
  // Their match library must instead be built from the club's teams
  // (`GET /api/admin/teams`, which is club-host-scoped for a club_admin), then
  // read per-team via the scoped path (the backend has club-admin READ parity).
  const isClubAdmin = !!me?.is_club_admin;
  const meTeams = me?.teams ?? [];

  const unscopedMatchesQuery = useQuery({
    queryKey: unscopedKey("admin-matches"),
    queryFn: () => apiFetch<Match[]>("/api/matches", { scope: false }),
    enabled: isGlobalAdmin,
  });
  const adminTeamsQuery = useQuery({
    queryKey: unscopedKey("admin-teams"),
    queryFn: getAdminTeams,
    enabled: isGlobalAdmin || isClubAdmin,
    staleTime: 60_000,
  });
  const matchTeamOptions = useMemo(
    () =>
      isGlobalAdmin || isClubAdmin
        ? normalizeTeamOptions(adminTeamsQuery.data ?? [])
        : normalizeTeamOptions(
            meTeams.filter((team) => team.capabilities.includes("match:write")),
          ),
    [adminTeamsQuery.data, isGlobalAdmin, isClubAdmin, meTeams],
  );
  const matchTeamIds = useMemo(
    () =>
      matchTeamOptions
        .map((team) => team.id)
        .sort()
        .join("|"),
    [matchTeamOptions],
  );
  const allowedMatchesQuery = useQuery({
    queryKey: unscopedKey("admin-matches-allowed", { teamIds: matchTeamIds }),
    queryFn: async () => {
      const perTeam = await Promise.all(
        matchTeamOptions.map((team) =>
          apiFetch<Match[]>("/api/matches", {
            scope: { teamId: team.id, seasonId: null },
          }),
        ),
      );
      const byId = new Map<string, Match>();
      for (const rows of perTeam) {
        for (const match of rows) byId.set(match.id, match);
      }
      return Array.from(byId.values());
    },
    enabled: !isGlobalAdmin && matchTeamOptions.length > 0,
  });
  const matchesQuery = isGlobalAdmin
    ? unscopedMatchesQuery
    : allowedMatchesQuery;
  const matches = matchesQuery.data ?? [];
  const librarySignals = useMemo(() => {
    let ready = 0;
    let missingVideo = 0;
    let failed = 0;
    let processing = 0;
    let recentUploads = 0;
    const recentCutoffMs = Date.now() - 7 * 24 * 60 * 60 * 1000;
    for (const match of matches) {
      const slots = slotsForMatch(match);
      const statuses = slots.map(
        (slot) => match.video_status?.[slot] ?? "none",
      );
      const hasVideo = Object.values(match.videos ?? {}).some(Boolean);
      const touchedAt = Date.parse(match.updated_at || match.created_at || "");
      if (statuses.every((status) => status === "ready")) ready += 1;
      if (
        hasVideo &&
        Number.isFinite(touchedAt) &&
        touchedAt >= recentCutoffMs
      ) {
        recentUploads += 1;
      }
      if (
        slots.some((slot) => !match.videos?.[slot]) ||
        statuses.some((status) => status === "none")
      ) {
        missingVideo += 1;
      }
      if (statuses.some((status) => status === "error")) failed += 1;
      if (
        statuses.some(
          (status) => status === "transcoding" || status === "processing",
        )
      ) {
        processing += 1;
      }
    }
    return { ready, missingVideo, failed, processing, recentUploads };
  }, [matches]);
  const teamNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of matchTeamOptions) map.set(t.id, t.name);
    return map;
  }, [matchTeamOptions]);

  // Gate transcode-progress polling on "any visible slot transcoding".
  const anyTranscoding = useMemo(() => {
    for (const m of matches) {
      for (const status of Object.values(m.video_status ?? {})) {
        if (status === "transcoding") return true;
      }
    }
    return false;
  }, [matches]);
  useTranscodeProgress({ enabled: anyTranscoding });

  // B2 — filters.
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "none" | "ready" | "transcoding" | "error" | "partial"
  >("all");
  const [matchStatusFilter, setMatchStatusFilter] = useState<
    "all" | "scheduled" | "completed" | "postponed" | "cancelled"
  >("all");
  const [competitionFilter, setCompetitionFilter] = useState<
    "all" | "league" | "tournament" | "friendly"
  >("all");
  const [formatFilter, setFormatFilter] = useState<"all" | "full" | "separate">(
    "all",
  );
  const [teamFilter, setTeamFilter] = useState<string>("all");
  const [seasonFilter, setSeasonFilter] = useState<string>("all");
  const [sort, setSort] = useState<"date_asc" | "date_desc" | "updated_desc">(
    "date_desc",
  );
  const [pageSize, setPageSize] = useState("5");
  const [page, setPage] = useState(1);

  // Season options come from the globally-shared `me.seasons` list —
  // the same source `SeasonLabel` resolves names from, so no extra
  // fetch. The default season is marked so the operator can tell which
  // one new matches inherit.
  const seasons = useAuthStore((s) => s.me?.seasons ?? EMPTY_SEASONS);
  const seasonOptions = useMemo(
    () => [
      { value: "all", label: "All seasons" },
      ...seasons.map((s) => ({
        value: s.id,
        label: s.is_default ? `${s.name} (default)` : s.name,
      })),
    ],
    [seasons],
  );

  const [expanded, setExpanded] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<Match | "new" | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Match | null>(null);
  // Sequential multi-upload queue: per-row "Upload video" adds an item.
  // The queue lives in `UploadQueueProvider` at AdminShell level so it
  // (and the docked panel) survives navigation between admin sections.
  const uploadQueue = useUploadQueueContext();
  const [queueTarget, setQueueTarget] = useState<{
    match: Match;
    slot?: UploadSlot;
  } | null>(null);

  const collectionControls = matches.length > 5;
  const filtered = useMemo(() => {
    if (!collectionControls) return matches;
    const term = search.trim().toLowerCase();
    const filtered = matches.filter((m) => {
      if (term) {
        const haystack = [
          m.home_team,
          m.away_team,
          m.location,
          m.id,
          m.date,
          m.stage,
          m.tournament_name,
          m.team_id ? teamNameById.get(m.team_id) : null,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      if (formatFilter !== "all" && deriveFormatGroup(m) !== formatFilter) {
        return false;
      }
      if (
        matchStatusFilter !== "all" &&
        (m.match_status ??
          (m.score_home != null && m.score_away != null
            ? "completed"
            : "scheduled")) !== matchStatusFilter
      ) {
        return false;
      }
      if (
        competitionFilter !== "all" &&
        (m.competition ?? "league") !== competitionFilter
      ) {
        return false;
      }
      if (statusFilter !== "all") {
        const agg = deriveAggregateStatus(m);
        if (agg !== statusFilter) return false;
      }
      if (teamFilter !== "all") {
        const tid = m.team_id ?? "__unassigned__";
        if (tid !== teamFilter) return false;
      }
      if (seasonFilter !== "all" && m.season_id !== seasonFilter) {
        return false;
      }
      return true;
    });

    const sorted = [...filtered];
    sorted.sort((a, b) => {
      if (sort === "updated_desc") {
        return (b.updated_at ?? "").localeCompare(a.updated_at ?? "");
      }
      const da = a.date || "";
      const db = b.date || "";
      return sort === "date_asc" ? da.localeCompare(db) : db.localeCompare(da);
    });
    return sorted;
  }, [
    collectionControls,
    matches,
    teamNameById,
    search,
    statusFilter,
    matchStatusFilter,
    competitionFilter,
    formatFilter,
    teamFilter,
    seasonFilter,
    sort,
  ]);

  useEffect(() => {
    setPage(1);
  }, [
    search,
    statusFilter,
    matchStatusFilter,
    competitionFilter,
    formatFilter,
    teamFilter,
    seasonFilter,
    sort,
    pageSize,
  ]);

  const pageSizeNumber = Number(pageSize);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSizeNumber));
  const safePage = Math.min(page, totalPages);
  const pageRows = useMemo(() => {
    if (!collectionControls) return matches;
    const start = (safePage - 1) * pageSizeNumber;
    return filtered.slice(start, start + pageSizeNumber);
  }, [collectionControls, matches, filtered, pageSizeNumber, safePage]);
  const firstVisible =
    filtered.length === 0 ? 0 : (safePage - 1) * pageSizeNumber + 1;
  const lastVisible = Math.min(filtered.length, safePage * pageSizeNumber);

  // Detect cross-tenant rows lacking a team_id so global admins can
  // filter for / spot orphaned matches that predate team scoping.
  // Team-scoped operators never see these because their reads are
  // fetched per allowed team.
  const hasUnassigned = useMemo(
    () => isGlobalAdmin && matches.some((m) => !m.team_id),
    [isGlobalAdmin, matches],
  );
  const teamFilterOptions = useMemo(
    () => [
      { value: "all", label: "All teams" },
      ...matchTeamOptions.map((t) => ({ value: t.id, label: t.name })),
      ...(hasUnassigned
        ? [{ value: "__unassigned__", label: "(no team)" }]
        : []),
    ],
    [hasUnassigned, matchTeamOptions],
  );
  useEffect(() => {
    if (teamFilter === "all") return;
    if (!teamFilterOptions.some((option) => option.value === teamFilter)) {
      setTeamFilter("all");
    }
  }, [teamFilter, teamFilterOptions]);

  const hasActiveFilters =
    search.trim() !== "" ||
    statusFilter !== "all" ||
    matchStatusFilter !== "all" ||
    competitionFilter !== "all" ||
    formatFilter !== "all" ||
    teamFilter !== "all" ||
    seasonFilter !== "all" ||
    sort !== "date_desc";

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setMatchStatusFilter("all");
    setCompetitionFilter("all");
    setFormatFilter("all");
    setTeamFilter("all");
    setSeasonFilter("all");
    setSort("date_desc");
    setPage(1);
  };

  return (
    <div data-testid="admin-matches" className="flex flex-col gap-3">
      <AdminPageHeader
        eyebrow="Media"
        title="Match Library"
        description="The club’s match film, ready for the right people."
        meta={
          matchesQuery.isFetching
            ? "Refreshing…"
            : `${firstVisible}-${lastVisible} of ${filtered.length}`
        }
        actions={
          <button
            type="button"
            onClick={() => setEditTarget("new")}
            disabled={matchTeamOptions.length === 0}
            data-testid="admin-matches-add"
            aria-label="Add match"
            title={
              matchTeamOptions.length === 0
                ? "No manageable teams available"
                : "Add match"
            }
            className="inline-flex h-8 w-8 items-center justify-center rounded border border-accent bg-accent text-accent-ink transition-colors hover:bg-accent-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-canvas disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Plus size={16} strokeWidth={2} aria-hidden="true" />
            <span className="admin-new-match-label">New match</span>
          </button>
        }
      />

      {matchesQuery.error ? (
        <p
          data-testid="admin-matches-error"
          className="text-destructive font-mono text-xs"
        >
          Failed to load matches.
        </p>
      ) : null}

      <section className="admin-match-library-pane">
        {collectionControls ? (
          <div className="admin-match-collection-toolbar" role="group" aria-label="Search and filter matches">
            <label className="admin-match-search-field">
              <span>Quick search</span>
              <span className="admin-match-search-control">
                <Search size={16} aria-hidden="true" />
                <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Team, tournament, stage or date" data-testid="admin-matches-search" />
              </span>
            </label>
            <MatchFilterField label="Squad">
              <TkticSelect value={teamFilter} onChange={setTeamFilter} data-testid="admin-matches-team-filter" size="sm" ariaLabel="Squad" options={teamFilterOptions.map((option) => ({ ...option, label: option.value === "all" ? "All" : option.label }))} />
            </MatchFilterField>
            <MatchFilterField label="Competition">
              <TkticSelect value={competitionFilter} onChange={(next) => setCompetitionFilter(next as typeof competitionFilter)} data-testid="admin-matches-competition-filter" size="sm" ariaLabel="Competition" options={[{ value: "all", label: "All" }, { value: "league", label: "League" }, { value: "tournament", label: "Tournament" }, { value: "friendly", label: "Friendly" }]} />
            </MatchFilterField>
            <MatchFilterField label="Match status">
              <TkticSelect value={matchStatusFilter} onChange={(next) => setMatchStatusFilter(next as typeof matchStatusFilter)} data-testid="admin-matches-match-status-filter" size="sm" ariaLabel="Match status" options={[{ value: "all", label: "All" }, { value: "scheduled", label: "Scheduled" }, { value: "completed", label: "Completed" }, { value: "postponed", label: "Postponed" }, { value: "cancelled", label: "Cancelled" }]} />
            </MatchFilterField>
            <MatchFilterField label="Recording">
              <TkticSelect value={statusFilter} onChange={(next) => setStatusFilter(next as typeof statusFilter)} data-testid="admin-matches-status-filter" size="sm" ariaLabel="Recording" options={[{ value: "all", label: "All" }, { value: "ready", label: "Ready" }, { value: "transcoding", label: "Processing" }, { value: "error", label: "Error" }, { value: "partial", label: "Partial" }, { value: "none", label: "Unavailable" }]} />
            </MatchFilterField>
            <MatchFilterField label="Season">
              <TkticSelect value={seasonFilter} onChange={setSeasonFilter} data-testid="admin-matches-season-filter" size="sm" ariaLabel="Season" options={seasonOptions.map((option) => ({ ...option, label: option.value === "all" ? "All" : option.label }))} />
            </MatchFilterField>
            <MatchFilterField label="Video format">
              <TkticSelect value={formatFilter} onChange={(next) => setFormatFilter(next as typeof formatFilter)} data-testid="admin-matches-format-filter" size="sm" ariaLabel="Video format" options={[{ value: "all", label: "All" }, { value: "full", label: "Full match" }, { value: "separate", label: "Separate recordings" }]} />
            </MatchFilterField>
            <button type="button" className="admin-match-clear-filters" disabled={!hasActiveFilters} onClick={clearFilters}>Clear filters</button>
          </div>
        ) : null}

        <DataTableShell className="admin-match-table-shell hidden md:block">
          <DataTableScroll>
            <DataTable data-testid="admin-matches-table">
              <DataTableHead><DataTableRow interactive={false}><DataTableHeadCell>Match</DataTableHeadCell></DataTableRow></DataTableHead>
              <DataTableBody>
                {filtered.length === 0 ? <DataTableEmptyRow colSpan={1} data-testid="admin-matches-empty">No matching results.</DataTableEmptyRow> : null}
                {pageRows.map((match) => {
                  const isOpen = expanded === match.id;
                  const teamName = match.team_id ? (teamNameById.get(match.team_id) ?? match.team_id) : null;
                  return <FragmentRow key={match.id} match={match} isOpen={isOpen} onToggle={() => setExpanded(isOpen ? null : match.id)} onEdit={() => setEditTarget(match)} onDelete={() => setDeleteTarget(match)} onUpload={() => setQueueTarget({ match })} showTeamColumn teamName={teamName} />;
                })}
              </DataTableBody>
            </DataTable>
          </DataTableScroll>
        </DataTableShell>
        <div className="admin-match-mobile-list md:hidden" data-testid="admin-matches-cards">
          {filtered.length === 0 ? <p data-testid="admin-matches-empty-mobile" className="admin-match-empty">No matching results.</p> : null}
          {pageRows.map((match) => {
            const isOpen = expanded === match.id;
            const teamName = match.team_id ? (teamNameById.get(match.team_id) ?? match.team_id) : null;
            return <AdminMatchCard key={match.id} match={match} isOpen={isOpen} onToggle={() => setExpanded(isOpen ? null : match.id)} onEdit={() => setEditTarget(match)} onDelete={() => setDeleteTarget(match)} onUpload={() => setQueueTarget({ match })} showTeamColumn teamName={teamName} />;
          })}
        </div>

        {collectionControls ? (
          <div className="admin-match-collection-footer">
            <span data-testid="admin-matches-pagination-summary">
              {firstVisible}–{lastVisible} of {filtered.length} matches
            </span>
            <div className="admin-match-footer-controls">
              <label className="admin-match-page-size admin-match-sort-field"><span>Sort</span><TkticSelect value={sort} onChange={(next) => setSort(next as typeof sort)} data-testid="admin-matches-sort" size="sm" ariaLabel="Sort" options={[{ value: "date_desc", label: "Newest" }, { value: "date_asc", label: "Oldest" }, { value: "updated_desc", label: "Recently updated" }]} /></label>
              <label className="admin-match-page-size"><span>Per page</span><TkticSelect value={pageSize} onChange={setPageSize} data-testid="admin-matches-page-size" size="sm" ariaLabel="Per page" options={PAGE_SIZE_OPTIONS.map((option) => ({ ...option, label: option.value }))} /></label>
            </div>
            <nav className="admin-match-pagination" aria-label="Match pages">
              <button type="button" onClick={() => setPage((value) => Math.max(1, value - 1))} disabled={safePage <= 1} data-testid="admin-matches-prev-page" aria-label="Previous page"><ChevronLeft size={16} aria-hidden="true" /></button>
              {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
                <button key={pageNumber} type="button" onClick={() => setPage(pageNumber)} aria-current={safePage === pageNumber ? "page" : undefined} data-testid={safePage === pageNumber ? "admin-matches-page-indicator" : undefined} aria-label={`Page ${pageNumber}`}>{pageNumber}</button>
              ))}
              <button type="button" onClick={() => setPage((value) => Math.min(totalPages, value + 1))} disabled={safePage >= totalPages} data-testid="admin-matches-next-page" aria-label="Next page"><ChevronRight size={16} aria-hidden="true" /></button>
            </nav>
          </div>
        ) : null}
      </section>

      {expanded ? (
        <MatchRecordingsDialog
          match={matches.find((match) => match.id === expanded) ?? null}
          teamName={(() => {
            const match = matches.find((candidate) => candidate.id === expanded);
            return match?.team_id ? (teamNameById.get(match.team_id) ?? match.team_id) : null;
          })()}
          onClose={() => setExpanded(null)}
          onUpload={(slot) => {
            const match = matches.find((candidate) => candidate.id === expanded);
            if (match) {
              setExpanded(null);
              setQueueTarget({ match, slot });
            }
          }}
        />
      ) : null}

      {/* B6 — add/edit match form (M12b: extracted into shared
       * `MatchEditModal` so the coach Matches tab + admin Matches page
       * share the same modal). */}
      {editTarget ? (
        <MatchEditModal
          match={editTarget === "new" ? null : editTarget}
          teamOptions={matchTeamOptions}
          knownTournaments={knownTournamentNames(matches)}
          onClose={() => setEditTarget(null)}
        />
      ) : null}

      {/* B7 — delete confirm */}
      {deleteTarget ? (
        <DeleteMatchConfirm
          match={deleteTarget}
          onClose={() => setDeleteTarget(null)}
        />
      ) : null}

      {/* Per-row "Upload video" → slot + file picker → adds to the queue.
       * The docked `UploadQueuePanel` is rendered by `AdminShell`, not
       * here, so it persists across admin route changes. */}
      {queueTarget ? (
        <AddToQueueModal
          match={queueTarget.match}
          initialSlot={queueTarget.slot}
          onAdd={(slot, file) => {
            uploadQueue.enqueue(queueTarget.match, slot, file);
            setQueueTarget(null);
          }}
          onClose={() => setQueueTarget(null)}
        />
      ) : null}
      <div className="admin-operational-summary grid gap-2 sm:grid-cols-2 xl:grid-cols-5">
        <AdminMetricTile
          label="Library matches"
          value={String(matches.length)}
          sub={`${filtered.length} visible after filters`}
          testid="admin-matches-signal-total"
        />
        <AdminMetricTile
          label="Ready video"
          value={String(librarySignals.ready)}
          sub="All expected slots playable"
          tone={librarySignals.ready > 0 ? "good" : "neutral"}
          testid="admin-matches-signal-ready"
        />
        <AdminMetricTile
          label="Recent uploads"
          value={String(librarySignals.recentUploads)}
          sub="Video touched in the last 7 days"
          tone={librarySignals.recentUploads > 0 ? "busy" : "neutral"}
          testid="admin-matches-signal-recent-uploads"
        />
        <AdminMetricTile
          label="Missing video"
          value={String(librarySignals.missingVideo)}
          sub="At least one expected slot empty"
          tone={librarySignals.missingVideo > 0 ? "warn" : "good"}
          testid="admin-matches-signal-missing"
        />
        <AdminMetricTile
          label="Needs recovery"
          value={String(librarySignals.failed)}
          sub={`${librarySignals.processing} processing now`}
          tone={
            librarySignals.failed > 0
              ? "warn"
              : librarySignals.processing > 0
                ? "busy"
                : "good"
          }
          testid="admin-matches-signal-recovery"
        />
      </div>
    </div>
  );
}

function MatchRecordingsDialog({
  match,
  teamName,
  onClose,
  onUpload,
}: {
  match: Match | null;
  teamName: string | null;
  onClose: () => void;
  onUpload: (slot: UploadSlot) => void;
}) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  if (!match) return null;

  return (
    <div
      className="admin-match-recordings-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="admin-match-recordings-title"
      data-testid="admin-matches-row-diagnostics"
      data-match-id={match.id}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="admin-match-recordings-dialog"
        data-recording-layout={deriveFormatGroup(match)}
      >
        <header>
          <h2 id="admin-match-recordings-title">Match recordings</h2>
          <button type="button" onClick={onClose} aria-label="Close match recordings">
            <X size={20} aria-hidden="true" />
          </button>
        </header>
        <div className="admin-match-recordings-body">
          <h3>{match.home_team || "—"} vs {match.away_team || "—"}</h3>
          <p className="admin-match-recordings-meta">
            {teamName ?? "No squad"} · {match.date || "No date"} · {recordingLayoutLabel(match)}
          </p>
          <MatchSlotPanel match={match} onUpload={onUpload} />
          <p className="admin-match-recordings-note">
            Recording uploads join the club upload queue. Extra time and
            shootout are independent optional recordings.
          </p>
        </div>
        <footer>
          <button type="button" onClick={onClose}>Close</button>
        </footer>
      </section>
    </div>
  );
}

/** Slot + file picker for adding one video to the upload queue. Kept
 * deliberately small — the heavy chunked-upload UX lives in the docked
 * `UploadQueuePanel`; this modal just captures {slot, file}.
 *
 * Focus handling mirrors `CoachMatchUploadModal`: focus the first field
 * on open, restore focus to the trigger on close, window-level Escape.
 * Tab is contained within the dialog so a keyboard user can't drift
 * onto the (aria-hidden-equivalent) background behind an `aria-modal`. */
function AddToQueueModal({
  match,
  initialSlot,
  onAdd,
  onClose,
}: {
  match: Match;
  initialSlot?: UploadSlot;
  onAdd: (slot: UploadSlot, file: File) => void;
  onClose: () => void;
}) {
  // Slot options follow the match's video layout — a `two_halves`
  // match has no `full` slot, so offering it would queue an upload
  // for a slot the match can't have. State seeds to the match's
  // first valid slot.
  const slotOptions = slotsForMatch(match);
  const [slot, setSlot] = useState<UploadSlot>(
    () => initialSlot ?? (defaultSlotForMatch(match) as UploadSlot),
  );
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLFormElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Initial focus + restore-on-close. The trigger is the row's
  // "Upload video" button; returning focus there keeps keyboard nav
  // coherent after the modal unmounts.
  useEffect(() => {
    previousFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    fileInputRef.current?.focus();
    const previousFocus = previousFocusRef.current;
    return () => {
      previousFocus?.focus();
    };
  }, []);

  // Window-level Escape so the modal closes even when focus has not
  // (yet) landed inside the overlay subtree.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Tab containment: wrap focus between the first and last focusable
  // element inside the dialog so an `aria-modal` user can't tab onto
  // the background.
  const onDialogKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
    if (event.key !== "Tab") return;
    const root = dialogRef.current;
    if (!root) return;
    const focusable = root.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!file) {
      setError("Pick a video file (.mp4 or .mkv) first.");
      return;
    }
    onAdd(slot, file);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Add match video to upload queue"
      data-testid="admin-add-to-queue-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className="admin-match-upload-overlay"
    >
      <form
        ref={dialogRef}
        onSubmit={onSubmit}
        onKeyDown={onDialogKeyDown}
        className="admin-match-upload-dialog"
      >
        <header>
          <h2>
            {match.home_team || "—"} vs {match.away_team || "—"} · {SLOT_LABELS[slot]}
          </h2>
          <button
            type="button"
            onClick={onClose}
            data-testid="admin-add-to-queue-close"
            aria-label="Close"
            title="Close"
          >
            <X size={20} strokeWidth={2} aria-hidden="true" />
          </button>
        </header>
        <div className="admin-match-upload-body">
          <div className="admin-match-upload-context">
            <div className="admin-match-upload-field">
              <span>Match</span>
              <div className="admin-match-upload-locked">
                <Film size={17} aria-hidden="true" />
                <strong>{match.home_team || "—"} vs {match.away_team || "—"}</strong>
              </div>
            </div>
            <label className="admin-match-upload-field">
              <span>Video segment</span>
              <TkticSelect
                value={slot}
                onChange={(next) => setSlot(next as UploadSlot)}
                data-testid="admin-add-to-queue-slot"
                ariaLabel="Video segment"
                className="w-full"
                options={slotOptions.map((value) => ({
                  value,
                  label: SLOT_LABELS[value],
                }))}
              />
            </label>
          </div>
          <p className="admin-match-upload-help">
            {deriveFormatGroup(match) === "separate"
              ? "Add each available recording to its matching slot. Extra time and penalty shootout are optional."
              : "Use one file containing the whole match, including extra time or penalties if recorded."}
          </p>
          <label className="admin-match-upload-file">
            <span>Recording file</span>
            <input
              ref={fileInputRef}
              type="file"
              accept=".mp4,.mkv,video/mp4,video/x-matroska"
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null);
                setError(null);
              }}
              data-testid="admin-add-to-queue-file"
            />
            {file ? (
              <small>{file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB</small>
            ) : null}
          </label>
          <p className="admin-match-upload-help">
            Choose an MP4 or MKV recording. It will be added to the persistent club upload queue.
          </p>
          {error ? (
            <p role="alert" data-testid="admin-add-to-queue-error" className="admin-match-upload-error">
              {error}
            </p>
          ) : null}
        </div>
        <footer>
          <button
            type="button"
            onClick={onClose}
            className="admin-match-upload-cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!file}
            data-testid="admin-add-to-queue-submit"
            className="admin-match-upload-submit"
          >
            Add to queue
          </button>
        </footer>
      </form>
    </div>
  );
}

function FragmentRow({
  match,
  isOpen,
  onToggle,
  onEdit,
  onDelete,
  onUpload,
  showTeamColumn,
  teamName,
}: {
  match: Match;
  isOpen: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onUpload: () => void;
  showTeamColumn: boolean;
  teamName: string | null;
}) {
  const aggStatus = deriveAggregateStatus(match);
  const resolvedMatchStatus =
    match.match_status ??
    (match.score_home != null && match.score_away != null
      ? "completed"
      : "scheduled");
  const competition = match.competition ?? "league";
  const competitionMeta = [
    competition === "league"
      ? "League"
      : (competitionLabel(competition) ?? "League"),
    match.tournament_name,
    match.stage,
    resolvedMatchStatus.replace(/^./, (letter) => letter.toUpperCase()),
  ].filter(Boolean);

  return (
    <>
      <DataTableRow
        data-testid="admin-matches-row"
        data-match-id={match.id}
        data-aggregate-status={aggStatus}
        className="admin-match-approved-row"
      >
        <DataTableCell>
          <div className="admin-match-row-layout">
            {match.has_thumbnail ? (
              <img
                src={`/api/matches/${encodeURIComponent(match.id)}/thumbnail`}
                alt=""
                loading="lazy"
                className="admin-match-thumbnail"
                onError={(event) => {
                  event.currentTarget.style.visibility = "hidden";
                }}
              />
            ) : (
              <div className="admin-match-thumbnail admin-match-thumbnail-empty" aria-hidden="true" />
            )}
            <div className="admin-match-row-copy">
              <p
                className="admin-match-row-eyebrow"
                data-testid="admin-matches-row-team"
                data-team-id={match.team_id ?? ""}
              >
                {showTeamColumn ? (teamName ?? "No squad") : null}
                {showTeamColumn ? <span aria-hidden="true"> · </span> : null}
                {match.date || "No date"}
              </p>
              <h3>
                {match.home_team || "—"} <span>vs</span> {match.away_team || "—"}{" "}
                <strong className="admin-match-row-score">
                  {match.score_home == null || match.score_away == null
                    ? "—"
                    : `${match.score_home}–${match.score_away}`}
                </strong>
              </h3>
              <p className="admin-match-row-meta">{competitionMeta.join(" · ")}</p>
              {shootoutLabel(match) ? (
                <p className="admin-match-row-shootout">{shootoutLabel(match)}</p>
              ) : null}
              <div className="admin-match-row-secondary">
                {match.location ? (
                  <>
                    <span>{match.location}</span>
                    <span aria-hidden="true"> · </span>
                  </>
                ) : null}
                <span>{recordingLayoutLabel(match)}</span>
                <span aria-hidden="true"> · </span>
                <SeasonLabel match={match} />
              </div>
            </div>
            <div className="admin-match-row-actions">
              <span className={`admin-match-video-badge admin-match-video-${aggStatus}`}>
                Video · {recordingStatusLabel(aggStatus)}
              </span>
              <button
                type="button"
                onClick={onToggle}
                aria-expanded={isOpen}
                data-testid="admin-matches-row-expand"
                className="admin-match-media-slots"
              >
                Media slots
                {isOpen ? <ChevronUp size={14} aria-hidden="true" /> : null}
              </button>
              <button type="button" onClick={onEdit} data-testid="admin-matches-row-edit" className="admin-match-edit-action">
                Edit
              </button>
              <MatchOperationsMenu match={match} onUpload={onUpload} onDelete={onDelete} />
            </div>
          </div>
        </DataTableCell>
      </DataTableRow>
    </>
  );
}

function MatchOperationsMenu({
  match,
  onUpload,
  onDelete,
}: {
  match: Match;
  onUpload: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", close);
    window.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", close);
      window.removeEventListener("keydown", escape);
    };
  }, [open]);

  return (
    <div className="admin-match-operations" ref={rootRef}>
      <button
        type="button"
        className="admin-match-more-action"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        data-testid="admin-matches-row-more"
      >
        More
      </button>
      {open ? (
        <div className="admin-match-operations-menu" role="menu">
          <a
            href={`/match/${match.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            role="menuitem"
            data-testid="admin-matches-row-open"
          >
            <ExternalLink size={15} aria-hidden="true" /> Open match
          </a>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onUpload();
            }}
            data-testid="admin-matches-row-upload"
          >
            <Upload size={15} aria-hidden="true" /> Upload video
          </button>
          <button
            type="button"
            role="menuitem"
            className="admin-match-delete-action"
            onClick={() => {
              setOpen(false);
              onDelete();
            }}
            data-testid="admin-matches-row-delete"
          >
            <Trash2 size={15} aria-hidden="true" /> Delete match
          </button>
        </div>
      ) : null}
    </div>
  );
}

function AdminMatchCard({
  match,
  isOpen,
  onToggle,
  onEdit,
  onDelete,
  onUpload,
  showTeamColumn,
  teamName,
}: {
  match: Match;
  isOpen: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onUpload: () => void;
  showTeamColumn: boolean;
  teamName: string | null;
}) {
  // Slots come from the match's video LAYOUT (`format`), not from
  // whatever keys `video_status` happens to carry — a pre-migration
  // `two_halves` match may still have a stale `full` key, and we
  // must not surface it.
  const slots = slotsForMatch(match);
  const aggStatus = deriveAggregateStatus(match);

  return (
    <article
      data-testid="admin-matches-card"
      data-match-id={match.id}
      data-aggregate-status={aggStatus}
      className="rounded border border-rule bg-surface shadow-sm p-3"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-mono text-[0.65rem] uppercase tracking-wider text-ink-muted">
            {match.date || "No date"}
            {match.time ? <span> · {match.time}</span> : null}
          </p>
          <h3 className="mt-1 font-display text-base text-ink">
            {match.home_team || "—"} <span className="text-ink-muted">vs</span>{" "}
            {match.away_team || "—"}
          </h3>
          {match.location ? (
            <p className="mt-0.5 text-xs text-ink-muted">{match.location}</p>
          ) : null}
          {showTeamColumn ? (
            <p
              data-testid="admin-matches-card-team"
              data-team-id={match.team_id ?? ""}
              className="mt-1 text-xs text-ink-muted"
            >
              Team: <span className="text-ink">{teamName ?? "no team"}</span>
            </p>
          ) : null}
          <SeasonLabel match={match} />
        </div>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isOpen}
          aria-label={isOpen ? "Collapse diagnostics" : "Expand diagnostics"}
          title={isOpen ? "Collapse diagnostics" : "Expand diagnostics"}
          data-testid="admin-matches-card-expand"
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded border border-rule text-ink-muted transition-colors hover:bg-field hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
        >
          {isOpen ? (
            <ChevronUp size={16} strokeWidth={2} aria-hidden="true" />
          ) : (
            <ChevronDown size={16} strokeWidth={2} aria-hidden="true" />
          )}
        </button>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
        <div className="rounded border border-rule/60 bg-elevated px-2 py-1.5">
          <span className="block font-display text-[0.6rem] uppercase tracking-wider text-ink-muted">
            Format
          </span>
          <FormatPill format={match.format} />
        </div>
        <div className="rounded border border-rule/60 bg-elevated px-2 py-1.5">
          <span className="block font-display text-[0.6rem] uppercase tracking-wider text-ink-muted">
            Score
          </span>
          <span className="font-mono text-ink">
            {match.score_home == null || match.score_away == null
              ? "—"
              : `${match.score_home}–${match.score_away}${
                  shootoutLabel(match) ? ` ${shootoutLabel(match)}` : ""
                }`}
          </span>
        </div>
        <div className="rounded border border-rule/60 bg-elevated px-2 py-1.5">
          <span className="block font-display text-[0.6rem] uppercase tracking-wider text-ink-muted">
            Slots
          </span>
          <div className="mt-1 flex flex-wrap gap-1">
            {slots.length === 0 ? (
              <span className="text-ink-muted text-[0.6rem]">none</span>
            ) : (
              slots.map((slot) => (
                <SlotPill
                  key={slot}
                  slot={slot}
                  status={match.video_status?.[slot] ?? "none"}
                />
              ))
            )}
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-1 border-t border-rule/60 pt-2">
        <AdminIconLink
          href={`/match/${match.slug}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="admin-matches-card-open"
          label="Open match"
          icon={ExternalLink}
        />
        <AdminIconButton
          onClick={onEdit}
          data-testid="admin-matches-card-edit"
          label="Edit match"
          icon={Pencil}
        />
        <AdminIconButton
          onClick={onUpload}
          data-testid="admin-matches-card-upload"
          label="Upload video"
          icon={Upload}
          tone="accent"
        />
        <AdminIconButton
          onClick={onDelete}
          data-testid="admin-matches-card-delete"
          label="Delete match"
          icon={Trash2}
          tone="destructive"
        />
      </div>

    </article>
  );
}

/** Inline season label rendered under the matchup cell. Seasons are
 * globally shared, so the name is resolved from the /api/me payload's
 * top-level `seasons` array. Cheap — no extra fetch. */
function SeasonLabel({ match }: { match: Match }) {
  const seasonName = useAuthStore((s) => {
    if (!s.me || !match.season_id) return null;
    return s.me.seasons.find((sn) => sn.id === match.season_id)?.name ?? null;
  });
  if (!seasonName) return null;
  return <span data-testid="admin-matches-row-season">{seasonName}</span>;
}

function recordingLayoutLabel(match: Match): string {
  return deriveFormatGroup(match) === "separate"
    ? "Separate recordings"
    : "Full match";
}

function recordingStatusLabel(
  status: ReturnType<typeof deriveAggregateStatus>,
): string {
  switch (status) {
    case "ready":
      return "Ready";
    case "transcoding":
      return "Processing";
    case "error":
      return "Error";
    case "partial":
      return "Partial";
    default:
      return "Unavailable";
  }
}

function FormatPill({ format }: { format: string }) {
  const display =
    format === "two_halves" || format === "separate"
      ? "Separate recordings"
      : format === "full"
        ? "Full"
        : format;
  return (
    <span className="rounded border border-rule bg-surface px-1.5 py-0.5 text-[0.6rem] uppercase tracking-wider text-ink-muted">
      {display}
    </span>
  );
}

function SlotPill({ slot, status }: { slot: string; status: string }) {
  const tone =
    status === "ready"
      ? "border-good/40 text-good"
      : status === "transcoding"
        ? "border-accent text-accent"
        : status === "error"
          ? "border-destructive/40 text-destructive"
          : "border-rule text-ink-muted";
  return (
    <span
      data-testid="admin-matches-slot-pill"
      data-slot={slot}
      data-slot-status={status}
      className={`rounded border bg-surface px-1.5 py-0.5 text-[0.6rem] font-mono ${tone}`}
    >
      {slot.replace("_", " ")} · {status}
    </span>
  );
}

async function invalidateAdminMatches(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: unscopedKey("public-matches") }),
    queryClient.invalidateQueries({ queryKey: unscopedKey("admin-matches") }),
    queryClient.invalidateQueries({
      queryKey: unscopedKey("admin-matches-allowed"),
    }),
    queryClient.invalidateQueries({ queryKey: ["admin-matches-scoped"] }),
    queryClient.invalidateQueries({ queryKey: ["coach-matches"] }),
    // The club Overview aggregates match/slot state (admin-console-split).
    queryClient.invalidateQueries({ queryKey: unscopedKey("club-overview") }),
  ]);
}

function DeleteMatchConfirm({
  match,
  onClose,
}: {
  match: Match;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await apiFetch(`/api/matches/${encodeURIComponent(match.id)}`, {
        method: "DELETE",
        scope: false,
      });
      await invalidateAdminMatches(queryClient);
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(
          typeof err.detail === "string"
            ? err.detail
            : "Failed to delete match.",
        );
      } else {
        setError("Failed to delete match.");
      }
      setSubmitting(false);
    }
  };

  return (
    <ConfirmDialog
      title="Delete match?"
      message={
        <>
          <p>
            <strong>
              {match.home_team || "—"} vs {match.away_team || "—"}
            </strong>{" "}
            ({match.date || "no date"}) will be permanently removed along with
            all uploaded video, HLS variants, and coaching notes referencing it.
          </p>
          {error ? (
            <p
              data-testid="admin-matches-delete-error"
              className="mt-2 text-destructive font-mono text-xs"
            >
              {error}
            </p>
          ) : null}
        </>
      }
      confirmLabel={submitting ? "Deleting…" : "Delete match"}
      danger
      onConfirm={onConfirm}
      onClose={onClose}
      data-testid="admin-matches-delete-confirm"
    />
  );
}

/** Aggregate a match's per-slot statuses into a single bucket the
 * filter dropdown can use. */
function deriveAggregateStatus(
  match: Match,
): "none" | "ready" | "transcoding" | "error" | "partial" {
  const statuses = Object.values(match.video_status ?? {});
  if (statuses.length === 0) return "none";
  if (statuses.every((s) => s === "ready")) return "ready";
  if (statuses.some((s) => s === "transcoding")) return "transcoding";
  if (statuses.some((s) => s === "error")) return "error";
  return "partial";
}

function deriveFormatGroup(match: Match): "full" | "separate" | "other" {
  if (match.format === "full") return "full";
  if (match.format === "two_halves" || match.format === "separate")
    return "separate";
  return "other";
}
